import { apiFetch, APIGEN_API_KEY } from '../utils/api';
import type {
  ApigenHealth,
  ApiCallRecord,
  ApiCallResponse,
  ApiCallsResponse,
  ApiEndpointModel,
  ApiJobCostResponse,
  ApiModelsResponse,
  ApiPlayerStatsResponse,
  ApiStatsResponse,
  ApiTraceRow,
  ApiTracesResponse,
  ModelTierConfig,
  TierOverride,
  StressAnalytics,
  StressOptions,
  StressTest,
  StressTestCreatedResponse,
  StressTestCreateRequest,
  StressTestListResponse,
} from './types';

function apigenHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...(extra || {}) };
  if (APIGEN_API_KEY) {
    headers['X-API-Key'] = APIGEN_API_KEY;
  }
  return headers;
}

/**
 * A generation call whose HTTP connection dropped (or was aborted) before a
 * response arrived. The run itself may well have completed server-side — the
 * apigen service records the row *after* the provider call finishes — so the
 * caller can attempt to recover the result from the call history.
 */
export class ApigenConnectionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ApigenConnectionError';
  }
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  const text = await res.text();
  let parsed: unknown = undefined;
  try {
    parsed = text ? JSON.parse(text) : undefined;
  } catch {
    // Non-JSON body (usually an HTML error/index page). This most often means
    // the request never reached the apigen service — e.g. the service isn't
    // running, or a dev proxy routed `/apigen/*` to the wrong backend.
    const snippet = text.slice(0, 80).replace(/\s+/g, ' ').trim();
    throw new Error(
      `apigen service unreachable or misconfigured (HTTP ${res.status}, non-JSON response` +
        (snippet ? `: "${snippet}…"` : '') +
        '). Is the apigen server running and VITE_APIGEN_API_BASE / the dev proxy set correctly?',
    );
  }
  if (!res.ok) {
    const body = parsed as { detail?: string; error?: string } | undefined;
    throw new Error(`${res.status}: ${body?.detail || body?.error || res.statusText}`);
  }
  return parsed as T;
}

/**
 * Liveness of the apigen service itself. It is a separate process from the
 * lightning backend, so its health has to be polled on its own base URL
 * (`/apigen/health`) rather than lightning's `/health`.
 */
export async function fetchApigenHealth(): Promise<ApigenHealth> {
  const res = await apiFetch('/health', { app: 'api', headers: apigenHeaders() });
  return jsonOrThrow<ApigenHealth>(res);
}

/** Fetch the configured endpoint breakdown (drives the section UI). */
export async function fetchModels(): Promise<ApiEndpointModel[]> {
  const res = await apiFetch('/v1/models', { app: 'api', headers: apigenHeaders() });
  const data = await jsonOrThrow<ApiModelsResponse>(res);
  return data.models;
}

/**
 * Fetch one endpoint's four tier setups, the defaults they fall back to, and
 * which knobs are tunable on it.
 */
export async function fetchTierConfig(modelKey: string): Promise<ModelTierConfig> {
  const res = await apiFetch(`/v1/models/${encodeURIComponent(modelKey)}/tiers`, {
    app: 'api',
    headers: apigenHeaders(),
  });
  return jsonOrThrow<ModelTierConfig>(res);
}

/**
 * Replace an endpoint's tier setups. The whole set is sent at once, so a tier
 * left out is saved as "no overrides" rather than keeping its previous value.
 * Unlike the pricing overrides, this changes how the server actually generates.
 */
export async function saveTierConfig(
  modelKey: string,
  tiers: Record<string, TierOverride>,
): Promise<ModelTierConfig> {
  const res = await apiFetch(`/v1/models/${encodeURIComponent(modelKey)}/tiers`, {
    app: 'api',
    method: 'PUT',
    headers: apigenHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ tiers }),
  });
  return jsonOrThrow<ModelTierConfig>(res);
}

/** Drop an endpoint's saved setups, reverting every tier to the shipped defaults. */
export async function resetTierConfig(modelKey: string): Promise<ModelTierConfig> {
  const res = await apiFetch(`/v1/models/${encodeURIComponent(modelKey)}/tiers`, {
    app: 'api',
    method: 'DELETE',
    headers: apigenHeaders(),
  });
  return jsonOrThrow<ModelTierConfig>(res);
}

/**
 * List recorded calls (most recent first), optionally filtered to a single
 * endpoint by its `model_type` (== the model `key`) and/or to one origin
 * (`manual` = run from the Playground, `api` = any other client). Drives
 * Request History.
 */
export async function fetchCalls(
  modelType?: string,
  limit = 200,
  source?: string,
): Promise<ApiCallRecord[]> {
  const params = new URLSearchParams();
  if (modelType) params.set('model_type', modelType);
  if (source) params.set('source', source);
  params.set('limit', String(limit));
  const res = await apiFetch(`/v1/calls?${params.toString()}`, {
    app: 'api',
    headers: apigenHeaders(),
  });
  const data = await jsonOrThrow<ApiCallsResponse>(res);
  return data.calls;
}

/**
 * Every recorded call belonging to one scan or one upstream job, oldest first.
 *
 * Same endpoint as `fetchCalls`, narrowed server-side by `scan_id`/`job_id` and
 * re-ordered ascending, because a trace is read as the sequence the calls
 * actually happened in rather than newest-first like the history lists.
 */
export async function fetchTraceCalls(opts: {
  scanId?: string;
  jobId?: string;
  limit?: number;
}): Promise<ApiCallRecord[]> {
  const params = new URLSearchParams();
  if (opts.scanId) params.set('scan_id', opts.scanId);
  if (opts.jobId) params.set('job_id', opts.jobId);
  params.set('limit', String(opts.limit ?? 500));
  const res = await apiFetch(`/v1/calls?${params.toString()}`, {
    app: 'api',
    headers: apigenHeaders(),
  });
  const data = await jsonOrThrow<ApiCallsResponse>(res);
  return [...data.calls].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
}

/**
 * List the scans/jobs that sent calls to gen-api in a time window
 * (`GET /v1/traces`), most recently active first. Drives the trace browse list;
 * one row per `(scan_id, job_id)` pair.
 */
export async function fetchTraces(opts: {
  from: Date | number;
  to: Date | number;
  limit?: number;
}): Promise<ApiTraceRow[]> {
  const params = new URLSearchParams();
  params.set('from', new Date(opts.from).toISOString());
  params.set('to', new Date(opts.to).toISOString());
  if (opts.limit != null) params.set('limit', String(opts.limit));
  const res = await apiFetch(`/v1/traces?${params.toString()}`, {
    app: 'api',
    headers: apigenHeaders(),
  });
  const data = await jsonOrThrow<ApiTracesResponse>(res);
  return data.traces;
}

/**
 * Fetch server-side aggregated statistics for a time window. Returns a small,
 * fixed-size payload (totals, percentiles, per-bucket series, per-model
 * breakdown, recent errors, slowest) regardless of call volume, so it scales
 * well beyond what client-side aggregation over `/v1/calls` could handle.
 */
export async function fetchStats(opts: {
  from: Date | number;
  to: Date | number;
  bucketSeconds: number;
  modelType?: string;
}): Promise<ApiStatsResponse> {
  const params = new URLSearchParams();
  params.set('from', new Date(opts.from).toISOString());
  params.set('to', new Date(opts.to).toISOString());
  params.set('bucket', String(opts.bucketSeconds));
  if (opts.modelType) params.set('model_type', opts.modelType);
  const res = await apiFetch(`/v1/stats?${params.toString()}`, {
    app: 'api',
    headers: apigenHeaders(),
  });
  return jsonOrThrow<ApiStatsResponse>(res);
}

/**
 * Fetch gen-api spend aggregated by `job_id` for a time window
 * (`GET /v1/stats/by-job`). Used to correlate gen-api cost with the
 * lightning merch job whose id was stamped on each request.
 */
export async function fetchStatsByJob(opts: {
  from: Date | number;
  to: Date | number;
}): Promise<ApiJobCostResponse> {
  const params = new URLSearchParams();
  params.set('from', new Date(opts.from).toISOString());
  params.set('to', new Date(opts.to).toISOString());
  const res = await apiFetch(`/v1/stats/by-job?${params.toString()}`, {
    app: 'api',
    headers: apigenHeaders(),
  });
  return jsonOrThrow<ApiJobCostResponse>(res);
}

/**
 * Fetch usage aggregated by player for a time window
 * (`GET /v1/stats/by-player`). Reports each player's calls, jobs, per-job
 * averages and endpoint split; QA load is excluded server-side.
 */
export async function fetchStatsByPlayer(opts: {
  from: Date | number;
  to: Date | number;
}): Promise<ApiPlayerStatsResponse> {
  const params = new URLSearchParams();
  params.set('from', new Date(opts.from).toISOString());
  params.set('to', new Date(opts.to).toISOString());
  const res = await apiFetch(`/v1/stats/by-player?${params.toString()}`, {
    app: 'api',
    headers: apigenHeaders(),
  });
  return jsonOrThrow<ApiPlayerStatsResponse>(res);
}

/** Fetch a single call record by id (used to poll an async run to completion). */
export async function getCall(recordId: string): Promise<ApiCallRecord> {
  const res = await apiFetch(`/v1/calls/${encodeURIComponent(recordId)}`, {
    app: 'api',
    headers: apigenHeaders(),
  });
  return jsonOrThrow<ApiCallRecord>(res);
}

/**
 * Overall ceiling for a generation. Image endpoints (especially GPT Image 2)
 * routinely take 30-60s; we keep polling up to this long before giving up.
 */
const RUN_TIMEOUT_MS = 320_000;
/** The async kick-off POST is short (uploads inputs + inserts a pending row). */
const START_TIMEOUT_MS = 60_000;
/** How often to poll the record while a run is pending. */
const POLL_INTERVAL_MS = 2_000;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * A run is only finished once it reaches a *terminal* status. The async
 * contract has two in-flight states — `pending` (row inserted, awaiting a
 * worker) and `processing` (a worker claimed it and the provider is running) —
 * and only `success` / `error` are terminal. Treating `processing` as done
 * would surface a half-finished row (no `output_ref` yet) as a completed run.
 */
function isTerminalStatus(status: string): boolean {
  return status !== 'pending' && status !== 'processing';
}

/**
 * Start a generation job. Uses the async contract (`?wait=false`): the service
 * uploads inputs, writes a `pending` row and returns its `record_id` almost
 * immediately, so this HTTP request is short and can't be severed mid-flight by
 * an intermediate proxy/gateway timeout. Older servers that ignore `wait`
 * return a terminal result here instead, which is passed straight through.
 */
async function startRun(path: string, body: Record<string, unknown>): Promise<ApiCallResponse> {
  const sep = path.includes('?') ? '&' : '?';
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), START_TIMEOUT_MS);
  try {
    const res = await apiFetch(`${path}${sep}wait=false`, {
      app: 'api',
      method: 'POST',
      headers: apigenHeaders({
        'Content-Type': 'application/json',
        // Marks the row as a hand-run from the Playground so Request History can
        // tell it apart from integration traffic. Deliberately absent from the
        // cURL snippets in the Docs tab.
        'X-Apigen-Source': 'manual',
      }),
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    return await jsonOrThrow<ApiCallResponse>(res);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApigenConnectionError(
        'Timed out starting the generation. The apigen service may still have ' +
          'accepted it — check the Requests tab before retrying.',
      );
    }
    if (err instanceof TypeError) {
      throw new ApigenConnectionError(
        'Network error contacting the apigen service — the connection dropped ' +
          'before the job started. It may still have been accepted; check the ' +
          'Requests tab before retrying.',
      );
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/** Poll a pending record until it reaches a terminal status (or we time out). */
async function pollUntilDone(
  recordId: string,
  { intervalMs = POLL_INTERVAL_MS, timeoutMs = RUN_TIMEOUT_MS }: { intervalMs?: number; timeoutMs?: number } = {},
): Promise<ApiCallResponse> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await delay(intervalMs);
    try {
      const record = await getCall(recordId);
      if (isTerminalStatus(record.status)) return callRecordToResponse(record);
    } catch {
      // Transient polling failure (proxy hiccup, brief restart) — keep trying;
      // the record is already persisted server-side.
    }
  }
  throw new ApigenConnectionError(
    'Timed out waiting for the generation to finish. It may still complete — ' +
      'check the Requests tab.',
  );
}

/**
 * Run one endpoint end-to-end: start the async job, then poll the record until
 * it finishes. Because no single request stays open for the whole generation,
 * a slow provider can no longer be cut off by a proxy timeout mid-call.
 */
export async function runEndpoint(
  path: string,
  body: Record<string, unknown>,
): Promise<ApiCallResponse> {
  const started = await startRun(path, body);
  // Only a terminal result (from an older server that ran synchronously) is
  // returned directly; a `pending`/`processing` row still needs polling.
  if (isTerminalStatus(started.status)) return started;
  return pollUntilDone(started.record_id);
}

/**
 * Rebuild the Playground result envelope from a stored call record. The record
 * keeps the output as an S3 reference (`output_ref`), not inline base64, so a
 * recovered result shows the image via its URL.
 */
function callRecordToResponse(record: ApiCallRecord): ApiCallResponse {
  return {
    record_id: record.id,
    status: record.status,
    model_type: record.model_type,
    model_id: record.model_id,
    provider: record.provider,
    output_image: null,
    output_url: record.output_ref,
    cost_usd: record.cost_usd,
    elapsed_ms: record.duration_seconds != null ? Math.round(record.duration_seconds * 1000) : null,
    error: record.error,
  };
}

/**
 * Safety net for a kick-off POST whose connection dropped before returning the
 * `record_id`. The service still wrote a row, so the job surfaces as a *new*
 * history record (an id absent from `knownIds`, snapshotted just before the
 * run). Find it, then poll it to completion if it's still pending.
 */
export async function recoverDroppedCall(
  modelType: string,
  knownIds: Set<string>,
  { attempts = 10, intervalMs = 2000 }: { attempts?: number; intervalMs?: number } = {},
): Promise<ApiCallResponse | null> {
  for (let i = 0; i < attempts; i += 1) {
    try {
      const calls = await fetchCalls(modelType, 20);
      const fresh = calls.find((c) => !knownIds.has(c.id));
      if (fresh) {
        return isTerminalStatus(fresh.status) ? callRecordToResponse(fresh) : pollUntilDone(fresh.id);
      }
    } catch {
      // Ignore transient failures while the service comes back; keep polling.
    }
    if (i < attempts - 1) {
      await delay(intervalMs);
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Stress tests
// ---------------------------------------------------------------------------

/** Static options for the stress submit form (endpoints, limits, capabilities). */
export async function fetchStressOptions(): Promise<StressOptions> {
  const res = await apiFetch('/v1/stress/config', { app: 'api', headers: apigenHeaders() });
  return jsonOrThrow<StressOptions>(res);
}

/**
 * Create a stress test and start it. Returns immediately with the run's id and
 * the configuration as it will *actually* run — the backend clamps the knobs
 * and may fall back to synchronous submits, so this can differ from what was
 * submitted. Any such adjustment is spelled out in `warnings`.
 */
export async function createStressTest(
  body: StressTestCreateRequest,
): Promise<StressTestCreatedResponse> {
  const res = await apiFetch('/v1/stress', {
    app: 'api',
    method: 'POST',
    headers: apigenHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  return jsonOrThrow<StressTestCreatedResponse>(res);
}

/** List stress tests, most recent first. */
export async function fetchStressTests(limit = 50): Promise<StressTest[]> {
  const res = await apiFetch(`/v1/stress?limit=${limit}`, { app: 'api', headers: apigenHeaders() });
  const data = await jsonOrThrow<StressTestListResponse>(res);
  return data.tests;
}

/** One stress test, including live lightning batch progress when it has one. */
export async function fetchStressTest(id: string): Promise<StressTest> {
  const res = await apiFetch(`/v1/stress/${encodeURIComponent(id)}`, {
    app: 'api',
    headers: apigenHeaders(),
  });
  return jsonOrThrow<StressTest>(res);
}

/** Server-side aggregates over every gen-api call a stress test produced. */
export async function fetchStressAnalytics(
  id: string,
  bucketSeconds = 10,
): Promise<StressAnalytics> {
  const res = await apiFetch(
    `/v1/stress/${encodeURIComponent(id)}/analytics?bucket=${bucketSeconds}`,
    { app: 'api', headers: apigenHeaders() },
  );
  return jsonOrThrow<StressAnalytics>(res);
}

/**
 * Ask a running stress test to stop. Cooperative — the runner notices between
 * bursts, so calls already submitted still run to completion.
 */
export async function cancelStressTest(id: string): Promise<{ status: string; cancelled: boolean }> {
  const res = await apiFetch(`/v1/stress/${encodeURIComponent(id)}/cancel`, {
    app: 'api',
    method: 'POST',
    headers: apigenHeaders(),
  });
  return jsonOrThrow<{ status: string; cancelled: boolean }>(res);
}

/**
 * Read a File as a base64 string (without the `data:` prefix). The backend
 * accepts either raw base64 or a full data URL, but stripping keeps payloads
 * lean.
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
